import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Building2, Building, Snowflake, Recycle, Wheat } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const FICHES_CEE = [
  {
    code: "BAT-TH-163",
    icon: Building2,
    title: "Pompe à chaleur de type air/eau",
    text: "Installation d'une pompe à chaleur air/eau dans un bâtiment tertiaire existant, en remplacement ou en complément de la chaufferie.",
    testid: "fiche-card-163",
  },
  {
    code: "BAT-TH-134",
    icon: Snowflake,
    title: "Régulation haute pression flottante",
    text: "Système de régulation sur un groupe de production de froid permettant d'avoir une haute pression flottante, pour réduire les consommations électriques de la production de froid.",
    testid: "fiche-card-134",
  },
  {
    code: "BAT-TH-139",
    icon: Recycle,
    title: "Récupération de chaleur sur groupe de froid",
    text: "Récupération de la chaleur rejetée par un groupe de production de froid pour la valoriser : chauffage, eau chaude sanitaire ou besoins de process.",
    testid: "fiche-card-139",
  },
  {
    code: "BAR-TH-179",
    icon: Building,
    title: "Pompe à chaleur collective de type air/eau",
    text: "Installation d'une pompe à chaleur collective air/eau en bâtiment résidentiel collectif existant, par un professionnel RGE.",
    testid: "fiche-card-179",
  },
  {
    code: "AGRI-EQ-110",
    icon: Wheat,
    title: "Séchage solaire par insufflation",
    text: "Installation d'un système de séchage solaire par insufflation — panneaux solaires hybrides photovoltaïques et thermiques — pour les produits et co-produits agricoles et forestiers : fourrages, céréales, fruits ou bois.",
    testid: "fiche-card-agri",
  },
];

export default function Fiches() {
  return (
    <div data-testid="fiches-page">
      <section className="relative overflow-hidden border-b border-gold/10">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gold mb-4">Fiches CEE</p>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight text-white leading-[1.15]">
              Les opérations standardisées que <span className="gold-text">nous valorisons</span> pour vous
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300 font-light">
              Cinq fiches d'opérations standardisées d'économies d'énergie, au cœur de notre
              expertise : nous identifions votre éligibilité, montons vos dossiers et valorisons
              vos certificats auprès des obligataires.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16" data-testid="fiches-list">
        <div className="grid md:grid-cols-2 gap-6">
          {FICHES_CEE.map((f, i) => (
            <Reveal key={f.code} delay={i * 0.08}>
              <div
                data-testid={f.testid}
                className="h-full bg-navy-800/50 border border-gold/15 rounded-3xl p-8 hover:border-gold/45 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-gold/10 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                    <f.icon size={22} className="text-gold-bright" />
                  </div>
                  <span className="text-xs font-semibold tracking-[0.15em] text-navy-950 bg-gradient-to-r from-gold to-gold-bright px-3 py-1.5 rounded-full">
                    {f.code}
                  </span>
                </div>
                <h3 className="text-xl font-medium tracking-wide text-gold-bright mb-3">{f.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24" data-testid="simulator-defense-section">
        <Reveal>
          <div className="bg-navy-800/50 border border-gold/15 rounded-3xl p-8 sm:p-12 grid lg:grid-cols-[auto_1fr] gap-8 items-center">
            <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0 mx-auto lg:mx-0">
              <ShieldCheck size={30} className="text-gold-bright" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-serif font-semibold text-white mb-3">
                Vos CEE, nous les valorisons pour vous.
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-slate-400">
                Les dispositifs CEE peuvent être complexes et nécessitent de respecter de nombreuses
                exigences. Levi One vous accompagne à chaque étape pour structurer votre dossier,
                identifier les solutions les plus adaptées à votre projet et faciliter la mise en
                relation avec les bons interlocuteurs.
              </p>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">
                De l'analyse de votre projet au suivi des démarches, nous veillons à la bonne
                coordination des différents acteurs et à la conformité de votre dossier, avec un
                objectif simple : vous permettre d'avancer sereinement, avec un accompagnement de
                confiance à chaque étape.
              </p>
              <Link
                to="/contact"
                data-testid="fiches-cta-btn"
                className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-gold to-gold-bright hover:from-gold-bright hover:to-gold text-navy-950 font-semibold px-7 py-3.5 rounded-full shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5"
              >
                Demander une étude <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
